/**
 * LABELS — what each cylinder wears, and the proof it can be read.
 *
 * Its own screen rather than a tab on the register, because the WORK is a different shape:
 * the register is about one cylinder at a time and this is about a run of thirty, taken to
 * a printer, checked against the vessels and confirmed. The passport links to one
 * cylinder's own labels; this is where they are made.
 *
 * THE ORDER OF THE PAGE IS THE ORDER OF THE WORK (feedback: action before information):
 * what is waiting for a label, then the runs, then the stock, then the scans that prove the
 * thing works at all.
 *
 * NOTHING HERE HAS BEEN PRINTED ON A REAL PRINTER OR SCANNED WITH A REAL PHONE. The page
 * says so, in its own words, at the top — because a screen that looks finished is how a
 * feature gets trusted before anybody has held one.
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session, type Views } from '../api.js';
import { useAsk, useRun } from '../hooks.js';
import { Banner, Button, Card, Kpi, Loading, Pill, Register, Text, TextArea, Select, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { FleetTabs } from '../fleet.js';
import { count, date, when, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('labels')!.messages;

type Tab = 'waiting' | 'runs' | 'stock' | 'scans';

export function Labels({ session }: { session: Session }) {
  const [tab, setTab] = useState<Tab>('waiting');
  return (
    <Page title="Labels" sub="The permanent CTS number on the vessel, and the code that opens its passport.">
      <FleetTabs active="labels" session={session} />
      {/* SAID OUT LOUD, AND FIRST. Everything below works on bytes and rows; whether a label
        * comes out of a real printer at the right size, and whether a phone reads it off a
        * curved drum in a yard, has not been tried. */}
      <Banner kind="warn" title="Pending physical UAT"
              detail="No label has yet been printed on real stock, on a real printer, or scanned with a yard phone. Until that is done, treat the sizes and the scanning as untested." />
      <div className="segmented">
        <button type="button" className={tab === 'waiting' ? 'on' : ''} onClick={() => setTab('waiting')}>Waiting for a label</button>
        <button type="button" className={tab === 'runs' ? 'on' : ''} onClick={() => setTab('runs')}>Print runs</button>
        <button type="button" className={tab === 'stock' ? 'on' : ''} onClick={() => setTab('stock')}>Label stock</button>
        <button type="button" className={tab === 'scans' ? 'on' : ''} onClick={() => setTab('scans')}>Scans</button>
      </div>
      {tab === 'waiting' ? <Waiting session={session} /> : null}
      {tab === 'runs' ? <Runs session={session} /> : null}
      {tab === 'stock' ? <Stock session={session} /> : null}
      {tab === 'scans' ? <Scans session={session} /> : null}
    </Page>
  );
}

/* ------------------------------------------------- what has no label yet */

/**
 * THE REQUESTS NOBODY COULD ANSWER, SHOWN RATHER THAN ACTED ON (Slice 3).
 *
 * Every cylinder registered since the beginning asked for a sticker, and nothing was ever on
 * the other end. A renderer exists now, and the danger that comes with it is precisely that
 * it sweeps up a year of back orders and a printer in a yard starts producing labels nobody
 * asked for this morning. The owner's instruction was explicit: present them, do not process
 * them.
 *
 * So this panel shows them, says which cylinders have since been labelled anyway, and offers
 * no button that acts on all of them. The way to deal with one is to tick its cylinder in
 * the list below and prepare a run — deliberately, with the stock chosen, like any other.
 */
function HistoricRequests() {
  const asked = useAsk(() => api.view('labels.historic', {}), [], MESSAGES);
  if (asked.loading) return null;
  if (asked.error) {
    return <Card title="Sticker requests from before this worked">
      <Banner kind="warn" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  }
  const h = asked.data!;
  if (h.rows.length === 0) return null;
  return (
    <Card title={`${count(h.rows.length)} sticker ${h.rows.length === 1 ? 'request was' : 'requests were'} made before this system could produce one`}
          sub="shown for a decision, not acted on — nothing here will print by itself"
          className="labels-historic">
      <Banner kind="info"
              title="These were asked for automatically when the cylinders were registered, and nothing answered them."
              detail="A renderer exists now. They have deliberately NOT been produced: printing a year of back orders at once was nobody's decision to make. To deal with one, tick its cylinder below and prepare a run." />
      <Register
        columns={[
          { key: 'cyl', head: 'Cylinder', cell: (r: Views['labels.historic']['result']['rows'][number]) => r.cylinderId
              ? <a href={`#/cylinders/${r.cylinderId}`}>{r.internalId ?? `#${r.cylinderId}`}</a>
              : <span className="muted">a cylinder that no longer exists</span> },
          { key: 'serial', head: 'Stamped on it', cell: (r: Views['labels.historic']['result']['rows'][number]) => r.serialNo ?? '—' },
          { key: 'asked', head: 'Asked for', cell: (r: Views['labels.historic']['result']['rows'][number]) => when(r.askedAt) },
          { key: 'need', head: 'Still wanted?', cell: (r: Views['labels.historic']['result']['rows'][number]) => r.stillWanted
              ? <Pill tone="testing">it has no label</Pill>
              : <Pill tone="good">already labelled since</Pill> },
        ]}
        rows={h.rows} empty="" />
      {h.someAlreadyLabelled
        ? <p className="muted">Some of these cylinders have been labelled since the request was made. Those need nothing.</p>
        : null}
    </Card>
  );
}

function Waiting({ session }: { session: Session }) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<number[]>([]);
  const [stockId, setStockId] = useState('');
  const asked = useAsk(() => api.view('labels.waiting', { page, pageSize: 50, ...(q ? { q } : {}) }), [page, q], MESSAGES);
  const stocks = useAsk(() => api.view('labels.stocks', {}), [], MESSAGES);
  const runner = useRun(MESSAGES);
  const may = can(session, 'label.prepare');

  type Row = Views['labels.waiting']['result']['rows'][number];
  const columns: readonly Column<Row>[] = [
    { key: 'pick', head: '', cell: (r) => (
        <input type="checkbox" checked={picked.includes(r.cylinderId)} aria-label={`include ${r.internalId}`}
               onChange={(e: { target: { checked: boolean } }) => {
                 const on = e.target.checked;
                 setPicked((v) => on ? [...v, r.cylinderId] : v.filter((x) => x !== r.cylinderId));
               }} />) },
    { key: 'internal', head: 'CTS number', cell: (r) => <a href={`#/cylinders/${r.cylinderId}`}>{r.internalId}</a> },
    { key: 'serial', head: 'Stamped on it', cell: (r) => r.serialNo },
    { key: 'gas', head: 'Gas', cell: (r) => words(r.gasService) },
    { key: 'branch', head: 'Yard', cell: (r) => r.branch ?? '—' },
    { key: 'since', head: 'Registered', cell: (r) => r.registeredOn ? date(r.registeredOn) : '—' },
  ];

  async function prepare() {
    // The key travels in the Idempotency-Key HEADER and the runner supplies it; a body
    // carrying one is refused outright. A dropped connection on this command costs paper.
    await runner.run((retryKey) => api.command('label.prepare', {
      stockId: Number(stockId), cylinderIds: picked,
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    setPicked([]);
    asked.reload();
  }

  return (
    <>
      <HistoricRequests />
      <Card title="Cylinders with no label yet"
            sub="derived, never flagged — a cylinder is here because it has no label, not because somebody said so"
            className="labels-waiting"
            actions={may ? <>
              <Select name="stock" label="" value={stockId} onChange={setStockId} placeholder="Choose the label stock"
                      options={(stocks.data?.rows ?? []).map((s) => ({ value: String(s.stockId), label: `${s.code} — ${s.name} (${s.labelsPerPage} a sheet)` }))} />
              <Button kind="primary" disabled={picked.length === 0 || !stockId || runner.busy}
                      onClick={() => { void prepare(); }}>
                {picked.length === 0 ? 'Choose some cylinders' : `Prepare ${count(picked.length)} ${picked.length === 1 ? 'label' : 'labels'}`}
              </Button>
            </> : null}>
        {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
        <div className="toolbar">
          <Text name="q" label="Find" value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="a CTS number or a serial" />
        </div>
        {asked.loading ? <Loading what="the fleet" /> : asked.error
          ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows}
                        empty="Every active cylinder has a label." />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
    </>
  );
}

/* ---------------------------------------------------------------- the runs */

function Runs({ session }: { session: Session }) {
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('labels.batches', { page, pageSize: 25 }), [page], MESSAGES);
  const [open, setOpen] = useState<number | null>(null);
  const runner = useRun(MESSAGES);
  const mayPrepare = can(session, 'label.prepare');
  const mayConfirm = can(session, 'label.confirm');

  type Row = Views['labels.batches']['result']['rows'][number];
  const columns: readonly Column<Row>[] = [
    { key: 'run', head: 'Run', cell: (r) => <button type="button" className="linklike" onClick={() => setOpen(open === r.batchId ? null : r.batchId)}>#{r.batchId}</button> },
    { key: 'stock', head: 'Stock', cell: (r) => <>{r.stockCode}<div className="muted">{r.printer ?? 'no printer named'}</div></> },
    { key: 'labels', head: 'Labels', right: true, cell: (r) => count(r.labels) },
    // THREE STATES, SHOWN AS THREE. "Prepared" is a choice, "generated" is a sheet that
    // exists, "printed" is a person's word that it came out of a machine. A screen that
    // showed one word for all three would be hiding the only one CTS cannot know.
    { key: 'state', head: 'Standing', cell: (r) => <Pill tone={r.status === 'printed' ? 'good' : r.status === 'abandoned' ? 'danger' : r.status === 'generated' ? 'primary' : 'empty'}>
        {r.status === 'prepared' ? 'chosen, no sheet yet'
          : r.status === 'generated' ? 'sheet made, not printed'
          : r.status === 'printed' ? 'printed' : 'abandoned'}</Pill> },
    { key: 'who', head: 'Prepared', cell: (r) => <>{r.preparedByName ?? '—'}<div className="muted">{r.preparedAt ? when(r.preparedAt) : ''}</div></> },
    { key: 'printed', head: 'Printed', cell: (r) => r.printedAt
        ? <>{r.printedByName ?? '—'}<div className="muted">{when(r.printedAt)}</div></>
        : <span className="muted">{r.abandonReason ? `abandoned — ${r.abandonReason}` : 'nobody has said so'}</span> },
    { key: 'do', head: '', cell: (r) => (
        <>
          <a className="btn small" href={`/documents/labels/preview/${r.batchId}.pdf`} target="_blank" rel="noreferrer">Preview</a>
          {mayPrepare && r.status === 'prepared'
            ? <Button small onClick={() => { void runner.run((k) => api.command('label.generate', { batchId: r.batchId }, k ? { idempotencyKey: k } : {})).then(() => asked.reload()); }}>Make the sheet</Button>
            : null}
          {mayConfirm && r.status === 'generated'
            ? <Button small kind="primary" onClick={() => { void runner.run((k) => api.command('label.confirmPrinted', { batchId: r.batchId }, k ? { idempotencyKey: k } : {})).then(() => asked.reload()); }}>I printed it</Button>
            : null}
        </>) },
  ];

  return (
    <Card title="Print runs" sub="chosen, made, printed — three things, recorded apart" className="labels-runs">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {asked.loading ? <Loading what="the runs" /> : asked.error
        ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
        : (<>
            <Register columns={columns} rows={asked.data!.rows} empty="No labels have been prepared yet." />
            <Pager page={asked.data!.page} onPage={setPage} />
            {open !== null ? <RunDetail batchId={open} session={session} onDone={() => { setOpen(null); asked.reload(); }} /> : null}
          </>)}
    </Card>
  );
}

/** What is on one run's sheet, label by label, with the exact payload each one carries. */
function RunDetail({ batchId, session, onDone }: { batchId: number; session: Session; onDone: () => void }) {
  const asked = useAsk(() => api.view('labels.batch', { batchId }), [batchId], MESSAGES);
  const runner = useRun(MESSAGES);
  const [reason, setReason] = useState('');
  if (asked.loading) return <Card title={`Run #${batchId}`}><Loading what="the run" /></Card>;
  if (asked.error) return <Card title={`Run #${batchId}`}><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  const d = asked.data!;
  return (
    <Card title={`Run #${batchId} — ${d.batch.labels} ${d.batch.labels === 1 ? 'label' : 'labels'}`}
          sub={`${d.batch.stockCode} · ${d.batch.printer ?? 'no printer named'}`} className="labels-run-detail">
      <Register
        columns={[
          { key: 'cyl', head: 'Cylinder', cell: (l: typeof d.labels[number]) => <a href={`#/cylinders/${l.cylinderId}`}>{l.internalId}</a> },
          { key: 'serial', head: 'Stamped on it', cell: (l: typeof d.labels[number]) => l.serialNo },
          { key: 'why', head: 'Why', cell: (l: typeof d.labels[number]) => <>{words(l.reason)}{l.note ? <div className="muted">{l.note}</div> : null}</> },
          // THE EXACT TEXT THE CODE CARRIES, shown. A person checking a label against a
          // cylinder can read what it will say rather than trusting that it is right.
          { key: 'payload', head: 'What the code says', cell: (l: typeof d.labels[number]) => <span className="serial">{l.qrPayload}</span> },
        ]}
        rows={d.labels} empty="This run has no labels on it." />
      {d.batch.status === 'prepared' && can(session, 'label.prepare') ? (
        <div className="toolbar" style={{ marginTop: 10 }}>
          <TextArea name="abandon" label="Abandon this run" value={reason} onChange={setReason}
                    placeholder="why it was not printed — the sheet jammed, the stock was wrong" />
          <Button kind="danger" disabled={!reason.trim() || runner.busy}
                  onClick={() => { void runner.run(() => api.command('label.abandon', { batchId, reason })).then(onDone); }}>
            Abandon it
          </Button>
        </div>
      ) : null}
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
    </Card>
  );
}

/* ---------------------------------------------------------------- the stock */

function Stock({ session }: { session: Session }) {
  const asked = useAsk(() => api.view('labels.stocks', { includeRetired: true }), [], MESSAGES);
  if (asked.loading) return <Card title="Label stock"><Loading what="the stock" /></Card>;
  if (asked.error) return <Card title="Label stock"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  return (
    <>
      <Card title="Label stock" sub="what the labels are, how they sit on a sheet, and what the printer can resolve"
            className="labels-stock">
        <Register
          columns={[
            { key: 'code', head: 'Code', cell: (s: Views['labels.stocks']['result']['rows'][number]) => <>{s.code}<div className="muted">{s.name}</div></> },
            { key: 'label', head: 'Label', cell: (s: Views['labels.stocks']['result']['rows'][number]) => `${s.widthMm} × ${s.heightMm} mm` },
            { key: 'sheet', head: 'On a sheet', cell: (s: Views['labels.stocks']['result']['rows'][number]) => `${s.columns} × ${s.rows} = ${s.labelsPerPage}` },
            { key: 'page', head: 'Sheet', cell: (s: Views['labels.stocks']['result']['rows'][number]) => `${s.pageWidthMm} × ${s.pageHeightMm} mm` },
            { key: 'printer', head: 'Printer', cell: (s: Views['labels.stocks']['result']['rows'][number]) => <>{s.printer ?? '—'}<div className="muted">{s.printer ? '' : 'not named'}</div></> },
            { key: 'dpi', head: 'Resolution', cell: (s: Views['labels.stocks']['result']['rows'][number]) => `${s.dpi} dpi` },
            { key: 'active', head: '', cell: (s: Views['labels.stocks']['result']['rows'][number]) => s.active ? <Pill tone="good">in use</Pill> : <Pill tone="muted">retired</Pill> },
          ]}
          rows={asked.data!.rows} empty="No label stock has been set up — a run cannot be prepared without it." />
      </Card>
      {can(session, 'label.configure') ? <ConfigureStock onDone={() => asked.reload()} /> : null}
    </>
  );
}

function ConfigureStock({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ code: '', name: '', widthMm: '', heightMm: '', columns: '', rows: '',
                               pageWidthMm: '210', pageHeightMm: '297', marginTopMm: '', marginLeftMm: '',
                               gapXMm: '', gapYMm: '', printer: '', dpi: '203' });
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  const runner = useRun(MESSAGES);
  const num = (v: string) => v.trim() === '' ? undefined : Number(v);
  return (
    <Card title="Set up label stock" sub="the measurements the sheet is laid out to" className="labels-configure">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="form-grid">
        <Text name="code" label="Short code" value={f.code} onChange={set('code')} required />
        <Text name="name" label="What it is called" value={f.name} onChange={set('name')} required />
        <Text name="widthMm" label="Label width (mm)" value={f.widthMm} onChange={set('widthMm')} required />
        <Text name="heightMm" label="Label height (mm)" value={f.heightMm} onChange={set('heightMm')} required />
        <Text name="columns" label="Across the sheet" value={f.columns} onChange={set('columns')} required />
        <Text name="rows" label="Down the sheet" value={f.rows} onChange={set('rows')} required />
        <Text name="pageWidthMm" label="Sheet width (mm)" value={f.pageWidthMm} onChange={set('pageWidthMm')} required />
        <Text name="pageHeightMm" label="Sheet height (mm)" value={f.pageHeightMm} onChange={set('pageHeightMm')} required />
        <Text name="marginTopMm" label="Top margin (mm)" value={f.marginTopMm} onChange={set('marginTopMm')} />
        <Text name="marginLeftMm" label="Left margin (mm)" value={f.marginLeftMm} onChange={set('marginLeftMm')} />
        <Text name="gapXMm" label="Gap across (mm)" value={f.gapXMm} onChange={set('gapXMm')} />
        <Text name="gapYMm" label="Gap down (mm)" value={f.gapYMm} onChange={set('gapYMm')} />
        <Text name="printer" label="Printer it is for" value={f.printer} onChange={set('printer')} />
        <Text name="dpi" label="Printer resolution (dpi)" value={f.dpi} onChange={set('dpi')}
              help="203 is an ordinary thermal label printer; 600 is a laser. Too few dots and the code will not scan." />
      </div>
      <Button kind="primary" disabled={runner.busy}
              onClick={() => { void runner.run(() => api.command('label.configureStock', {
                code: f.code, name: f.name,
                widthMm: Number(f.widthMm), heightMm: Number(f.heightMm),
                columns: Number(f.columns), rows: Number(f.rows),
                pageWidthMm: Number(f.pageWidthMm), pageHeightMm: Number(f.pageHeightMm),
                ...(num(f.marginTopMm) !== undefined ? { marginTopMm: num(f.marginTopMm)! } : {}),
                ...(num(f.marginLeftMm) !== undefined ? { marginLeftMm: num(f.marginLeftMm)! } : {}),
                ...(num(f.gapXMm) !== undefined ? { gapXMm: num(f.gapXMm)! } : {}),
                ...(num(f.gapYMm) !== undefined ? { gapYMm: num(f.gapYMm)! } : {}),
                ...(f.printer.trim() ? { printer: f.printer } : {}),
                ...(num(f.dpi) !== undefined ? { dpi: num(f.dpi)! } : {}),
              })).then(onDone); }}>Save this stock</Button>
    </Card>
  );
}

/* ---------------------------------------------------------------- the scans */

function Scans({ session }: { session: Session }) {
  const [unresolvedOnly, setUnresolved] = useState(false);
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('labels.scans', { page, pageSize: 50, ...(unresolvedOnly ? { unresolvedOnly: true } : {}) }),
                       [page, unresolvedOnly], MESSAGES);
  const [raw, setRaw] = useState('');
  const runner = useRun(MESSAGES);
  const may = can(session, 'label.scan');
  return (
    <Card title="Scans" sub="the proof a label can be read, which is not the same claim as having printed one"
          className="labels-scans"
          actions={<Button small onClick={() => { setUnresolved((v) => !v); setPage(1); }}>
            {unresolvedOnly ? 'Show every scan' : 'Only the ones that found nothing'}</Button>}>
      {may ? (
        <div className="toolbar">
          <Text name="raw" label="Scan or paste a code" value={raw} onChange={setRaw}
                placeholder="what the scanner read" />
          <Button disabled={!raw.trim() || runner.busy}
                  onClick={() => { void runner.run(() => api.command('label.scan', { raw })).then(() => { setRaw(''); asked.reload(); }); }}>
            Record it
          </Button>
        </div>
      ) : null}
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {asked.loading ? <Loading what="the scans" /> : asked.error
        ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
        : (<>
            <Register
              columns={[
                { key: 'what', head: 'What was read', cell: (s: Views['labels.scans']['result']['rows'][number]) => <span className="serial">{s.raw}</span> },
                { key: 'found', head: 'It opened', cell: (s: Views['labels.scans']['result']['rows'][number]) => s.resolved && s.cylinderId
                    ? <a href={`#/cylinders/${s.cylinderId}`}>{s.internalId}</a>
                    // THE MOST USEFUL ROW IN THIS TABLE. A sticker exists in the world that
                    // this system does not recognise: another installation's, a damaged code
                    // read wrongly, or a bug. All three are invisible if it is thrown away.
                    : <Pill tone="danger">nothing — this label is not ours</Pill> },
                { key: 'when', head: 'When', cell: (s: Views['labels.scans']['result']['rows'][number]) => when(s.scannedAt) },
                { key: 'who', head: 'Who', cell: (s: Views['labels.scans']['result']['rows'][number]) => s.scannedByName ?? '—' },
              ]}
              rows={asked.data!.rows}
              empty={unresolvedOnly ? 'Every scan so far has found its cylinder.' : 'Nothing has been scanned yet — so nothing proves these labels can be read.'} />
            <Pager page={asked.data!.page} onPage={setPage} />
          </>)}
    </Card>
  );
}
